import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';

// Ensure environment variables are available
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

// Create Supabase client with explicit configuration and retries
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    persistSession: true,
    storageKey: 'supabase.auth.token',
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'X-Client-Info': 'film-estimate-generator'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const checkSupabaseConnection = async () => {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Supabase credentials not found');
    return false;
  }

  let retries = 3;
  let lastError = null;

  while (retries > 0) {
    try {
      // Try a simple health check query first
      const { data, error: healthError } = await supabase
        .from('estimates')
        .select('count')
        .limit(1)
        .single();

      // PGRST116 means table doesn't exist yet, which is fine
      if (!healthError || healthError.code === 'PGRST116') {
        return true;
      }

      // If health check fails, try a fallback check
      const { data: versionData, error: fallbackError } = await supabase
        .rpc('version')
        .single();

      if (!fallbackError && versionData) {
        return true;
      }

      lastError = healthError || fallbackError;
      retries--;

      if (retries > 0) {
        await delay(1000); // Wait 1 second before retrying
        continue;
      }
    } catch (error) {
      console.error('Supabase connection check error:', error);
      lastError = error;
      retries--;

      if (retries > 0) {
        await delay(1000);
        continue;
      }
    }
  }

  if (lastError) {
    console.error('Supabase connection error:', lastError);
  }
  return false;
};

interface EstimateData {
  id: string;
  title: string;
  director?: string;
  actors: any[];
  territories: Record<string, any>;
  estimate_data: any;
}

export const saveEstimate = async (estimateData: EstimateData) => {
  try {
    // Validate required fields
    if (!estimateData.id) {
      throw new Error('Missing estimate ID');
    }
    if (!estimateData.title) {
      throw new Error('Missing estimate title');
    }
    if (!Array.isArray(estimateData.actors)) {
      throw new Error('Invalid actors data');
    }
    if (!estimateData.territories || typeof estimateData.territories !== 'object') {
      throw new Error('Invalid territories data');
    }
    if (!estimateData.estimate_data || typeof estimateData.estimate_data !== 'object') {
      throw new Error('Invalid estimate data');
    }

    // Validate the connection first
    const isConnected = await checkSupabaseConnection();
    if (!isConnected) {
      throw new Error('Database connection not available');
    }

    // Clean and prepare the data
    const cleanData = {
      id: estimateData.id,
      title: estimateData.title.trim(),
      director: estimateData.director?.trim() || null,
      actors: estimateData.actors,
      territories: estimateData.territories,
      estimate_data: estimateData.estimate_data,
      created_at: new Date().toISOString()
    };

    // Attempt to save the estimate with retry logic
    let retries = 3;
    let lastError = null;

    while (retries > 0) {
      try {
        const { data, error } = await supabase
          .from('estimates')
          .insert([cleanData])
          .select()
          .single();

        if (error) {
          throw error;
        }

        if (!data) {
          throw new Error('No data returned from insert operation');
        }

        return { data, error: null };
      } catch (error: any) {
        lastError = error;
        retries--;
        if (retries > 0) {
          await delay(1000);
          continue;
        }
        break;
      }
    }

    throw lastError || new Error('Failed to save estimate after multiple attempts');
  } catch (error: any) {
    console.error('Error saving estimate:', error);
    return { 
      data: null, 
      error: new Error(error.message || 'Failed to save estimate')
    };
  }
};